import React, { useEffect, useMemo, useState, useRef } from 'react';
import { apiRequest } from '../api/client';

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
};

// Pills per DESIGN.md: 4px radius, 11px label, semantic faint fill; the word always travels with the color.
const PILL = 'inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium';
const PILL_DANGER = 'bg-red-700/10 text-red-700 dark:bg-red-400/15 dark:text-red-400';
const PILL_WARNING = 'bg-amber-700/10 text-amber-700 dark:bg-amber-400/15 dark:text-amber-400';
const PILL_PROGRESS = 'bg-progress-faint text-progress';
const PILL_FOREST = 'bg-forest-faint text-forest-ondark dark:bg-forest-faint-dark dark:text-forest';
const PILL_NEUTRAL = 'bg-moss text-ink dark:bg-moss-dark dark:text-ink-dark';
const PILL_MUTED = 'border border-line text-ink-muted dark:border-line-dark dark:text-ink-muted-dark';

const messagePriorityClass = (priority = 'medium') => {
  switch (priority) {
    case 'urgent':
      return PILL_DANGER;
    case 'high':
      return PILL_WARNING;
    case 'low':
      return PILL_MUTED;
    default:
      return PILL_NEUTRAL;
  }
};

const messageTypeClass = (type = 'info') => {
  switch (type) {
    case 'alert':
      return PILL_DANGER;
    case 'warning':
      return PILL_WARNING;
    case 'maintenance':
      return PILL_NEUTRAL;
    case 'announcement':
      return PILL_PROGRESS;
    default:
      return PILL_FOREST;
  }
};

// Buttons per DESIGN.md: 6px radius, 600 weight, 44px min height, 120ms color, 2px focus ring.
const BTN = 'inline-flex items-center justify-center min-h-[44px] rounded-md px-3 text-xs sm:text-sm font-semibold transition-colors duration-120 ease-out active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60';
const BTN_PRIMARY = `${BTN} bg-forest-ondark text-white hover:bg-forest-ondark/90 dark:bg-forest dark:text-canvas-dark dark:hover:bg-forest-hover`;
const BTN_SECONDARY = `${BTN} border border-line text-ink hover:bg-moss dark:border-line-dark dark:text-ink-dark dark:hover:bg-moss-dark`;
const BTN_WARNING = `${BTN} border border-amber-700/40 text-amber-700 hover:bg-amber-700/10 dark:border-amber-400/40 dark:text-amber-400 dark:hover:bg-amber-400/10`;
const BTN_DANGER = `${BTN} border border-red-700/40 text-red-700 hover:bg-red-700/10 dark:border-red-400/40 dark:text-red-400 dark:hover:bg-red-400/10`;
const BTN_DANGER_FILLED = `${BTN} bg-red-700 text-white hover:bg-red-800 dark:bg-red-400 dark:text-canvas-dark dark:hover:bg-red-300`;
const CHECKBOX = 'h-4 w-4 rounded border-line text-forest-ondark focus:ring-forest dark:border-line-dark dark:bg-surface-dark';

function NotificationsPage() {
  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'english');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [waitingListItems, setWaitingListItems] = useState([]);
  const [systemMessages, setSystemMessages] = useState([]);
  const [ingredientReports, setIngredientReports] = useState([]);
  const [capabilities, setCapabilities] = useState({
    canDeactivateSystemMessages: false,
    canDeleteSystemMessages: false,
    canAdminFilterSystemMessages: false,
    canActivateSystemMessages: false,
  });
  const [systemMessagesTab, setSystemMessagesTab] = useState('active');
  const [messageActionId, setMessageActionId] = useState(null);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [selectedMessageIds, setSelectedMessageIds] = useState([]);

  const direction = language === 'hebrew' ? 'rtl' : 'ltr';

  useEffect(() => {
    const handleStorageChange = () => {
      const nextLanguage = localStorage.getItem('language') || 'english';
      setLanguage(nextLanguage);
      document.documentElement.dir = nextLanguage === 'hebrew' ? 'rtl' : 'ltr';
      document.documentElement.lang = nextLanguage === 'hebrew' ? 'he' : 'en';
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('languageChange', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('languageChange', handleStorageChange);
    };
  }, []);

  const translations = useMemo(() => ({
    english: {
      title: 'Notifications Center',
      subtitle: 'Waiting list, system messages, and ingredient issue reports',
      refresh: 'Refresh',
      loading: 'Loading...',
      errorPrefix: 'Could not load notifications',
      waitingListTitle: 'Potential Clients (Waiting List)',
      waitingListEmpty: 'No users in waiting list.',
      waitingListCreatedAt: 'Joined',
      waitingListGoal: 'Goal',
      waitingListPhone: 'Phone',
      waitingListMessage: 'Message',
      waitingListEmail: 'Email',
      systemMessagesTitle: 'System Messages',
      systemMessagesEmpty: 'No active system messages.',
      systemMessagesEmptyInactive: 'No inactive system messages.',
      systemMessagesEmptyAll: 'No system messages yet.',
      messageType: 'Type',
      messagePriority: 'Priority',
      messageWindow: 'Window',
      messageDirected: 'Directed To',
      directedToEveryone: 'Everyone (broadcast)',
      directedToUnknownProfile: 'Recipient not found',
      roleDietitian: 'Dietitian',
      roleCompanyManager: 'Manager',
      roleSysAdmin: 'System admin',
      messageContent: 'Content',
      ingredientReportsTitle: 'Ingredient Reports',
      ingredientReportsEmpty: 'No ingredient reports.',
      reportType: 'Report Type',
      reportFoodId: 'Food ID',
      reportBy: 'Reported By',
      reportCreated: 'Reported At',
      reportDescription: 'Description',
      reportSnapshot: 'Snapshot',
      unknown: 'Unknown',
      yes: 'Yes',
      no: 'No',
      waitingListCount: 'Waiting list',
      systemMessagesCount: 'Active messages',
      ingredientReportsCount: 'Ingredient reports',
      deactivateMessage: 'Done',
      deleteMessage: 'Delete',
      activateMessage: 'Activate',
      deactivateConfirm: 'Mark this message inactive? It will be hidden for everyone.',
      deleteConfirm: 'Permanently delete this message? This cannot be undone.',
      activateConfirm: 'Activate this message for everyone in its date window?',
      activateAllInactiveConfirm: 'Activate every inactive message?',
      deleteAllInactiveConfirm: 'Permanently delete all inactive messages? This cannot be undone.',
      deleteAllMessagesConfirm: 'Permanently delete EVERY system message? This cannot be undone.',
      actionError: 'Action failed',
      tabActive: 'Active',
      tabInactive: 'Inactive',
      tabAll: 'All',
      statusActive: 'Active',
      statusInactive: 'Inactive',
      bulkActivateAllInactive: 'Activate all inactive',
      bulkDeleteAllInactive: 'Delete all inactive',
      bulkDeleteAll: 'Delete all messages',
      bulkDeactivateAllActive: 'Deactivate all active',
      bulkDeleteAllActive: 'Delete all active',
      deactivateAllActiveConfirm: 'Set every active message to inactive?',
      deleteAllActiveConfirm: 'Permanently delete ALL active messages? This cannot be undone.',
      applySelected: 'Selection',
      selectedCount: 'selected',
      selectAllVisible: 'Select all',
      selectRow: 'Select this message',
      clearSelection: 'Clear',
      deleteSelected: 'Delete selected',
      activateSelected: 'Activate selected',
      deactivateSelected: 'Deactivate selected',
      applySelectedDeleteConfirm: 'Permanently delete selected messages?',
      applySelectedActivateConfirm: 'Activate selected inactive messages?',
      applySelectedDeactivateConfirm: 'Deactivate selected active messages?',
    },
    hebrew: {
      title: 'מרכז התראות',
      subtitle: 'רשימת המתנה, הודעות מערכת ודיווחי רכיבים',
      refresh: 'רענן',
      loading: 'טוען...',
      errorPrefix: 'לא ניתן לטעון התראות',
      waitingListTitle: 'לקוחות פוטנציאליים (רשימת המתנה)',
      waitingListEmpty: 'אין משתמשים ברשימת ההמתנה.',
      waitingListCreatedAt: 'נרשם בתאריך',
      waitingListGoal: 'מטרה',
      waitingListPhone: 'טלפון',
      waitingListMessage: 'הודעה',
      waitingListEmail: 'אימייל',
      systemMessagesTitle: 'הודעות מערכת',
      systemMessagesEmpty: 'אין הודעות מערכת פעילות.',
      systemMessagesEmptyInactive: 'אין הודעות מערכת לא פעילות.',
      systemMessagesEmptyAll: 'אין עדיין הודעות מערכת.',
      messageType: 'סוג',
      messagePriority: 'עדיפות',
      messageWindow: 'חלון זמן',
      messageDirected: 'מיועד ל',
      directedToEveryone: 'כולם (שידור)',
      directedToUnknownProfile: 'נמען לא נמצא',
      roleDietitian: 'דיאטנ/ית',
      roleCompanyManager: 'מנהל/ת חברה',
      roleSysAdmin: 'מנהל/ת מערכת',
      messageContent: 'תוכן',
      ingredientReportsTitle: 'דיווחי רכיבים',
      ingredientReportsEmpty: 'אין דיווחי רכיבים.',
      reportType: 'סוג דיווח',
      reportFoodId: 'מזהה מזון',
      reportBy: 'דווח על ידי',
      reportCreated: 'דווח בתאריך',
      reportDescription: 'תיאור',
      reportSnapshot: 'צילום מצב',
      unknown: 'לא ידוע',
      yes: 'כן',
      no: 'לא',
      waitingListCount: 'רשימת המתנה',
      systemMessagesCount: 'הודעות פעילות',
      ingredientReportsCount: 'דיווחי רכיבים',
      deactivateMessage: 'בוצע',
      deleteMessage: 'מחק',
      activateMessage: 'הפעל',
      deactivateConfirm: 'לסמן את ההודעה כלא פעילה? היא תוסתר לכולם.',
      deleteConfirm: 'למחוק לצמיתות את ההודעה? לא ניתן לבטל.',
      activateConfirm: 'להפעיל את ההודעה לכולם בחלון התאריכים שלה?',
      activateAllInactiveConfirm: 'להפעיל את כל ההודעות הלא פעילות?',
      deleteAllInactiveConfirm: 'למחוק לצמיתות את כל ההודעות הלא פעילות? לא ניתן לבטל.',
      deleteAllMessagesConfirm: 'למחוק לצמיתות את כל הודעות המערכת? לא ניתן לבטל.',
      actionError: 'הפעולה נכשלה',
      tabActive: 'פעילות',
      tabInactive: 'לא פעילות',
      tabAll: 'הכל',
      statusActive: 'פעילה',
      statusInactive: 'לא פעילה',
      bulkActivateAllInactive: 'הפעל את כל הלא פעילות',
      bulkDeleteAllInactive: 'מחק את כל הלא פעילות',
      bulkDeleteAll: 'מחק את כל ההודעות',
      bulkDeactivateAllActive: 'השבת את כל הפעילות',
      bulkDeleteAllActive: 'מחק את כל הפעילות',
      deactivateAllActiveConfirm: 'להשבית את כל ההודעות הפעילות?',
      deleteAllActiveConfirm: 'למחוק לצמיתות את כל ההודעות הפעילות? לא ניתן לבטל.',
      applySelected: 'בחירה',
      selectedCount: 'נבחרו',
      selectAllVisible: 'בחר הכל',
      selectRow: 'בחר הודעה זו',
      clearSelection: 'נקה',
      deleteSelected: 'מחק נבחרים',
      activateSelected: 'הפעל נבחרים',
      deactivateSelected: 'השבת נבחרים',
      applySelectedDeleteConfirm: 'למחוק לצמיתות את ההודעות הנבחרות?',
      applySelectedActivateConfirm: 'להפעיל את ההודעות הלא פעילות הנבחרות?',
      applySelectedDeactivateConfirm: 'להשבית את ההודעות הפעילות הנבחרות?',
    },
  }), []);

  const t = (key) => translations[language]?.[key] || translations.english[key] || key;

  const formatDirectedTo = (item) => {
    if (item.directed_to == null || item.directed_to === '') {
      return t('directedToEveryone');
    }
    const r = item.directed_to_recipient;
    if (!r) {
      return t('directedToUnknownProfile');
    }
    const roleLabel =
      r.role === 'company_manager'
        ? t('roleCompanyManager')
        : r.role === 'employee'
          ? t('roleDietitian')
          : r.role === 'sys_admin'
            ? t('roleSysAdmin')
            : r.role
              ? String(r.role)
              : '';

    const name = r.name && String(r.name).trim() ? String(r.name).trim() : '';
    const company = r.company_name && String(r.company_name).trim() ? String(r.company_name).trim() : '';

    let who = '';
    if (name && roleLabel) who = `${name} (${roleLabel})`;
    else if (name) who = name;
    else if (roleLabel) who = roleLabel;
    else if (company) who = company;
    else return t('directedToUnknownProfile');

    if (company && !who.includes(company)) {
      return `${who} — ${company}`;
    }
    return who;
  };

  const loadNotifications = async (tabOverride) => {
    setIsLoading(true);
    setError('');

    try {
      const effectiveTab = tabOverride !== undefined ? tabOverride : systemMessagesTab;
      const path = `/notifications?systemMessages=${encodeURIComponent(effectiveTab)}`;

      const data = await apiRequest(path);
      setWaitingListItems(data?.waitingList || []);
      setSystemMessages(data?.systemMessages || []);
      setIngredientReports(data?.ingredientReports || []);
      setCapabilities({
        canDeactivateSystemMessages: !!data?.capabilities?.canDeactivateSystemMessages,
        canDeleteSystemMessages: !!data?.capabilities?.canDeleteSystemMessages,
        canAdminFilterSystemMessages: !!data?.capabilities?.canAdminFilterSystemMessages,
        canActivateSystemMessages: !!data?.capabilities?.canActivateSystemMessages,
      });
    } catch (loadError) {
      console.error('Failed loading notifications', loadError);
      setError(loadError?.message || t('errorPrefix'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSelectedMessageIds([]);
  }, [systemMessagesTab]);

  const selectAllCheckboxRef = useRef(null);

  const selectedRows = useMemo(() => {
    const sel = new Set(selectedMessageIds);
    return systemMessages.filter((m) => sel.has(m.id));
  }, [systemMessages, selectedMessageIds]);

  const selectedActiveIds = useMemo(
    () => selectedRows.filter((m) => m.is_active).map((m) => m.id),
    [selectedRows]
  );
  const selectedInactiveIds = useMemo(
    () => selectedRows.filter((m) => !m.is_active).map((m) => m.id),
    [selectedRows]
  );

  const allVisibleSelected =
    systemMessages.length > 0 && selectedMessageIds.length === systemMessages.length;

  useEffect(() => {
    const el = selectAllCheckboxRef.current;
    if (!el) return;
    el.indeterminate =
      selectedMessageIds.length > 0 && selectedMessageIds.length < systemMessages.length;
  }, [selectedMessageIds, systemMessages.length]);

  const toggleMessageSelected = (id) => {
    setSelectedMessageIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAllSystemMessagesVisible = () => {
    setSelectedMessageIds(systemMessages.map((m) => m.id));
  };

  const clearMessageSelection = () => setSelectedMessageIds([]);

  const applyBulkApply = async (op, ids, confirmKey) => {
    if (!ids.length) return;
    if (!window.confirm(t(confirmKey))) return;
    setBulkActionLoading(true);
    setError('');
    try {
      await apiRequest('/system-messages/bulk/apply', { method: 'POST', body: { op, ids } });
      await loadNotifications();
      clearMessageSelection();
    } catch (e) {
      console.error('Bulk apply failed', e);
      setError(e?.message || t('actionError'));
    } finally {
      setBulkActionLoading(false);
    }
  };

  const bulkDeactivateAllActive = async () => {
    if (!window.confirm(t('deactivateAllActiveConfirm'))) return;
    setBulkActionLoading(true);
    setError('');
    try {
      await apiRequest('/system-messages/bulk', { method: 'PATCH', body: { op: 'deactivateActive' } });
      await loadNotifications();
      clearMessageSelection();
    } catch (e) {
      console.error('Bulk deactivate active failed', e);
      setError(e?.message || t('actionError'));
    } finally {
      setBulkActionLoading(false);
    }
  };

  const bulkDeleteAllActive = async () => {
    if (!window.confirm(t('deleteAllActiveConfirm'))) return;
    setBulkActionLoading(true);
    setError('');
    try {
      await apiRequest('/system-messages/bulk?scope=active', { method: 'DELETE' });
      await loadNotifications();
      clearMessageSelection();
    } catch (e) {
      console.error('Bulk delete active failed', e);
      setError(e?.message || t('actionError'));
    } finally {
      setBulkActionLoading(false);
    }
  };

  const deactivateSystemMessage = async (id) => {
    if (!window.confirm(t('deactivateConfirm'))) return;
    setMessageActionId(id);
    setError('');
    try {
      await apiRequest(`/system-messages/${encodeURIComponent(id)}/deactivate`, { method: 'PATCH' });
      await loadNotifications();
    } catch (e) {
      console.error('Deactivate message failed', e);
      setError(e?.message || t('actionError'));
    } finally {
      setMessageActionId(null);
    }
  };

  const deleteSystemMessage = async (id) => {
    if (!window.confirm(t('deleteConfirm'))) return;
    setMessageActionId(id);
    setError('');
    try {
      await apiRequest(`/system-messages/${encodeURIComponent(id)}`, { method: 'DELETE' });
      await loadNotifications();
    } catch (e) {
      console.error('Delete message failed', e);
      setError(e?.message || t('actionError'));
    } finally {
      setMessageActionId(null);
    }
  };

  const activateSystemMessage = async (id) => {
    if (!window.confirm(t('activateConfirm'))) return;
    setMessageActionId(id);
    setError('');
    try {
      await apiRequest(`/system-messages/${encodeURIComponent(id)}/activate`, { method: 'PATCH' });
      await loadNotifications();
    } catch (e) {
      console.error('Activate message failed', e);
      setError(e?.message || t('actionError'));
    } finally {
      setMessageActionId(null);
    }
  };

  const selectSystemMessagesTab = (tab) => {
    setSystemMessagesTab(tab);
    loadNotifications(tab);
  };

  const bulkActivateAllInactive = async () => {
    if (!window.confirm(t('activateAllInactiveConfirm'))) return;
    setBulkActionLoading(true);
    setError('');
    try {
      await apiRequest('/system-messages/bulk', { method: 'PATCH', body: { op: 'activateInactive' } });
      await loadNotifications();
    } catch (e) {
      console.error('Bulk activate failed', e);
      setError(e?.message || t('actionError'));
    } finally {
      setBulkActionLoading(false);
    }
  };

  const bulkDeleteInactive = async () => {
    if (!window.confirm(t('deleteAllInactiveConfirm'))) return;
    setBulkActionLoading(true);
    setError('');
    try {
      await apiRequest('/system-messages/bulk?scope=inactive', { method: 'DELETE' });
      await loadNotifications();
    } catch (e) {
      console.error('Bulk delete inactive failed', e);
      setError(e?.message || t('actionError'));
    } finally {
      setBulkActionLoading(false);
    }
  };

  const bulkDeleteAllMessages = async () => {
    if (!window.confirm(t('deleteAllMessagesConfirm'))) return;
    setBulkActionLoading(true);
    setError('');
    try {
      await apiRequest('/system-messages/bulk?scope=all', { method: 'DELETE' });
      await loadNotifications();
    } catch (e) {
      console.error('Bulk delete all failed', e);
      setError(e?.message || t('actionError'));
    } finally {
      setBulkActionLoading(false);
    }
  };

  const systemMessagesEmptyLabel = () => {
    if (!capabilities.canAdminFilterSystemMessages) return t('systemMessagesEmpty');
    if (systemMessagesTab === 'inactive') return t('systemMessagesEmptyInactive');
    if (systemMessagesTab === 'all') return t('systemMessagesEmptyAll');
    return t('systemMessagesEmpty');
  };

  const themeClasses = {
    pageBg: 'bg-canvas dark:bg-canvas-dark',
    cardBg: 'bg-surface border-line dark:bg-surface-dark dark:border-line-dark',
    textPrimary: 'text-ink dark:text-ink-dark',
    textSecondary: 'text-ink-muted dark:text-ink-muted-dark',
    textMuted: 'text-ink-muted dark:text-ink-muted-dark',
    tableHeader: 'bg-canvas text-ink-muted dark:bg-canvas-dark dark:text-ink-muted-dark',
    tableRow: 'border-line dark:border-line-dark hover:bg-canvas/60 dark:hover:bg-canvas-dark/60 transition-colors duration-120',
    tableCell: 'text-ink dark:text-ink-dark',
    sectionHead: 'border-b border-line dark:border-line-dark px-4 py-3',
  };

  return (
    <div className={`min-h-screen ${themeClasses.pageBg} pb-10 font-ui`} dir={direction}>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className={`font-display text-3xl sm:text-4xl font-medium leading-tight ${themeClasses.textPrimary}`}>{t('title')}</h1>
            <p className={`mt-1 text-sm ${themeClasses.textSecondary}`}>{t('subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={() => loadNotifications()}
            disabled={isLoading}
            className={BTN_SECONDARY}
          >
            {isLoading ? t('loading') : t('refresh')}
          </button>
        </div>

        <div className="mb-8 grid gap-3 sm:grid-cols-3">
          <div className={`rounded-lg border px-4 py-3 ${themeClasses.cardBg}`}>
            <p className={`text-[11px] font-medium uppercase tracking-wide ${themeClasses.textMuted}`}>{t('waitingListCount')}</p>
            <p className={`mt-1 text-2xl font-semibold tabular-nums ${themeClasses.textPrimary}`}>{waitingListItems.length}</p>
          </div>
          <div className={`rounded-lg border px-4 py-3 ${themeClasses.cardBg}`}>
            <p className={`text-[11px] font-medium uppercase tracking-wide ${themeClasses.textMuted}`}>{t('systemMessagesCount')}</p>
            <p className={`mt-1 text-2xl font-semibold tabular-nums ${themeClasses.textPrimary}`}>{systemMessages.length}</p>
          </div>
          <div className={`rounded-lg border px-4 py-3 ${themeClasses.cardBg}`}>
            <p className={`text-[11px] font-medium uppercase tracking-wide ${themeClasses.textMuted}`}>{t('ingredientReportsCount')}</p>
            <p className={`mt-1 text-2xl font-semibold tabular-nums ${themeClasses.textPrimary}`}>{ingredientReports.length}</p>
          </div>
        </div>

        {error && (
          <div role="alert" className="mb-6 rounded-lg border border-red-700/30 bg-red-700/5 p-3 text-sm text-red-700 dark:border-red-400/30 dark:bg-red-400/10 dark:text-red-400">
            {t('errorPrefix')}: {error}
          </div>
        )}

        <section className={`mb-6 rounded-lg border ${themeClasses.cardBg}`}>
          <div className={themeClasses.sectionHead}>
            <h2 className={`text-[15px] font-semibold ${themeClasses.textPrimary}`}>{t('waitingListTitle')}</h2>
          </div>
          {waitingListItems.length === 0 ? (
            <p className={`px-4 py-5 text-sm ${themeClasses.textMuted}`}>{t('waitingListEmpty')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-[13px]">
                <thead className={themeClasses.tableHeader}>
                  <tr>
                    <th className="px-4 py-2 text-start text-[11px] font-semibold uppercase tracking-wide">#</th>
                    <th className="px-4 py-2 text-start text-[11px] font-semibold uppercase tracking-wide">{t('waitingListCreatedAt')}</th>
                    <th className="px-4 py-2 text-start text-[11px] font-semibold uppercase tracking-wide">{language === 'hebrew' ? 'שם' : 'Name'}</th>
                    <th className="px-4 py-2 text-start text-[11px] font-semibold uppercase tracking-wide">{t('waitingListEmail')}</th>
                    <th className="px-4 py-2 text-start text-[11px] font-semibold uppercase tracking-wide">{t('waitingListPhone')}</th>
                    <th className="px-4 py-2 text-start text-[11px] font-semibold uppercase tracking-wide">{t('waitingListGoal')}</th>
                    <th className="px-4 py-2 text-start text-[11px] font-semibold uppercase tracking-wide">{t('waitingListMessage')}</th>
                  </tr>
                </thead>
                <tbody>
                  {waitingListItems.map((item, index) => (
                    <tr key={item.id} className={`border-t ${themeClasses.tableRow}`}>
                      <td className={`px-4 py-2.5 align-top ${themeClasses.tableCell}`}>{index + 1}</td>
                      <td className={`px-4 py-2.5 align-top ${themeClasses.tableCell}`}>{formatDateTime(item.created_at)}</td>
                      <td className={`px-4 py-2.5 align-top ${themeClasses.tableCell}`}>
                        {[item.first_name, item.last_name].filter(Boolean).join(' ') || t('unknown')}
                      </td>
                      <td className={`px-4 py-2.5 align-top ${themeClasses.tableCell}`}>{item.email || '-'}</td>
                      <td className={`px-4 py-2.5 align-top ${themeClasses.tableCell}`}>{item.phone || '-'}</td>
                      <td className={`px-4 py-2.5 align-top ${themeClasses.tableCell}`}>{item.goal || '-'}</td>
                      <td className={`px-4 py-2.5 align-top ${themeClasses.tableCell}`}>{item.message || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className={`mb-6 rounded-lg border ${themeClasses.cardBg}`}>
          <div className={`flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between ${themeClasses.sectionHead}`}>
            <h2 className={`text-[15px] font-semibold ${themeClasses.textPrimary}`}>{t('systemMessagesTitle')}</h2>
            {capabilities.canAdminFilterSystemMessages && (
              <div className="flex flex-wrap gap-2" role="tablist" aria-label={t('systemMessagesTitle')}>
                {(['active', 'inactive', 'all']).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    role="tab"
                    aria-selected={systemMessagesTab === tab}
                    onClick={() => selectSystemMessagesTab(tab)}
                    disabled={isLoading}
                    className={`min-h-[36px] rounded-md border px-3 text-xs font-semibold transition-colors duration-120 focus:outline-none focus-visible:ring-2 focus-visible:ring-forest disabled:cursor-not-allowed disabled:opacity-60 ${
                      systemMessagesTab === tab
                        ? 'border-forest/40 bg-forest-faint text-forest-ondark dark:bg-forest-faint-dark dark:text-forest'
                        : 'border-line text-ink hover:bg-moss dark:border-line-dark dark:text-ink-dark dark:hover:bg-moss-dark'
                    }`}
                  >
                    {tab === 'active' ? t('tabActive') : tab === 'inactive' ? t('tabInactive') : t('tabAll')}
                  </button>
                ))}
              </div>
            )}
          </div>

          {capabilities.canAdminFilterSystemMessages && (
            <div
              className="flex flex-col gap-3 border-b border-line bg-canvas px-4 py-3 dark:border-line-dark dark:bg-canvas-dark"
            >
              {systemMessages.length > 0 && (
                <div className="flex flex-wrap items-center gap-3">
                  <label
                    className={`inline-flex cursor-pointer items-center gap-2 text-xs font-semibold ${themeClasses.textPrimary}`}
                  >
                    <input
                      ref={selectAllCheckboxRef}
                      type="checkbox"
                      className={CHECKBOX}
                      checked={allVisibleSelected}
                      onChange={(e) =>
                        e.target.checked ? selectAllSystemMessagesVisible() : clearMessageSelection()
                      }
                    />
                    {t('selectAllVisible')}
                  </label>
                  <button
                    type="button"
                    onClick={clearMessageSelection}
                    disabled={!selectedMessageIds.length || bulkActionLoading || isLoading}
                    className="min-h-[36px] rounded-md px-2 text-xs font-semibold text-ink transition-colors duration-120 hover:bg-moss disabled:cursor-not-allowed disabled:opacity-50 dark:text-ink-dark dark:hover:bg-moss-dark"
                  >
                    {t('clearSelection')}
                  </button>
                  {selectedMessageIds.length > 0 && (
                    <span className={`text-xs ${themeClasses.textMuted}`}>
                      {selectedMessageIds.length} {t('selectedCount')}
                    </span>
                  )}
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {systemMessagesTab === 'all' && (
                  <>
                    <button
                      type="button"
                      onClick={bulkActivateAllInactive}
                      disabled={bulkActionLoading || isLoading}
                      className={BTN_PRIMARY}
                    >
                      {bulkActionLoading ? t('loading') : t('bulkActivateAllInactive')}
                    </button>
                    <button
                      type="button"
                      onClick={bulkDeactivateAllActive}
                      disabled={bulkActionLoading || isLoading}
                      className={BTN_WARNING}
                    >
                      {bulkActionLoading ? t('loading') : t('bulkDeactivateAllActive')}
                    </button>
                    <button
                      type="button"
                      onClick={bulkDeleteInactive}
                      disabled={bulkActionLoading || isLoading}
                      className={BTN_DANGER}
                    >
                      {bulkActionLoading ? t('loading') : t('bulkDeleteAllInactive')}
                    </button>
                    <button
                      type="button"
                      onClick={bulkDeleteAllMessages}
                      disabled={bulkActionLoading || isLoading}
                      className={BTN_DANGER_FILLED}
                    >
                      {bulkActionLoading ? t('loading') : t('bulkDeleteAll')}
                    </button>
                  </>
                )}
                {systemMessagesTab === 'inactive' && (
                  <>
                    <button
                      type="button"
                      onClick={bulkActivateAllInactive}
                      disabled={bulkActionLoading || isLoading}
                      className={BTN_PRIMARY}
                    >
                      {bulkActionLoading ? t('loading') : t('bulkActivateAllInactive')}
                    </button>
                    <button
                      type="button"
                      onClick={bulkDeleteInactive}
                      disabled={bulkActionLoading || isLoading}
                      className={BTN_DANGER}
                    >
                      {bulkActionLoading ? t('loading') : t('bulkDeleteAllInactive')}
                    </button>
                  </>
                )}
                {systemMessagesTab === 'active' && (
                  <>
                    <button
                      type="button"
                      onClick={bulkDeactivateAllActive}
                      disabled={bulkActionLoading || isLoading}
                      className={BTN_WARNING}
                    >
                      {bulkActionLoading ? t('loading') : t('bulkDeactivateAllActive')}
                    </button>
                    <button
                      type="button"
                      onClick={bulkDeleteAllActive}
                      disabled={bulkActionLoading || isLoading}
                      className={BTN_DANGER}
                    >
                      {bulkActionLoading ? t('loading') : t('bulkDeleteAllActive')}
                    </button>
                  </>
                )}
              </div>

              {selectedMessageIds.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 border-t border-line pt-3 dark:border-line-dark">
                  <span className={`text-[11px] font-semibold uppercase tracking-wide ${themeClasses.textMuted}`}>
                    {t('applySelected')}
                  </span>
                  {(systemMessagesTab === 'all' || systemMessagesTab === 'inactive') &&
                    selectedInactiveIds.length > 0 && (
                      <button
                        type="button"
                        onClick={() =>
                          applyBulkApply('activate', selectedInactiveIds, 'applySelectedActivateConfirm')
                        }
                        disabled={bulkActionLoading || isLoading}
                        className={BTN_PRIMARY}
                      >
                        {bulkActionLoading ? t('loading') : t('activateSelected')}
                      </button>
                    )}
                  {(systemMessagesTab === 'all' || systemMessagesTab === 'active') &&
                    selectedActiveIds.length > 0 && (
                      <button
                        type="button"
                        onClick={() =>
                          applyBulkApply('deactivate', selectedActiveIds, 'applySelectedDeactivateConfirm')
                        }
                        disabled={bulkActionLoading || isLoading}
                        className={BTN_WARNING}
                      >
                        {bulkActionLoading ? t('loading') : t('deactivateSelected')}
                      </button>
                    )}
                  <button
                    type="button"
                    onClick={() =>
                      applyBulkApply('delete', selectedMessageIds, 'applySelectedDeleteConfirm')
                    }
                    disabled={bulkActionLoading || isLoading}
                    className={BTN_DANGER}
                  >
                    {bulkActionLoading ? t('loading') : t('deleteSelected')}
                  </button>
                </div>
              )}
            </div>
          )}

          {systemMessages.length === 0 ? (
            <p className={`px-4 py-5 text-sm ${themeClasses.textMuted}`}>{systemMessagesEmptyLabel()}</p>
          ) : (
            <div className="space-y-2 p-4">
              {systemMessages.map((item) => {
                const showDeactivateRow =
                  item.is_active !== false &&
                  (capabilities.canDeactivateSystemMessages ||
                    (capabilities.canAdminFilterSystemMessages &&
                      capabilities.canDeleteSystemMessages));
                return (
                  <article
                    key={item.id}
                    className="flex gap-3 rounded-lg border border-line bg-surface p-4 dark:border-line-dark dark:bg-surface-dark"
                  >
                    {capabilities.canAdminFilterSystemMessages && (
                      <div className="shrink-0 pt-1">
                        <input
                          type="checkbox"
                          className={CHECKBOX}
                          checked={selectedMessageIds.includes(item.id)}
                          onChange={() => toggleMessageSelected(item.id)}
                          aria-label={t('selectRow')}
                        />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                          <h3 className={`text-[15px] font-semibold ${themeClasses.textPrimary}`}>{item.title}</h3>
                          {capabilities.canAdminFilterSystemMessages && systemMessagesTab === 'all' && (
                            <span
                              className={`${PILL} ${item.is_active ? PILL_FOREST : PILL_MUTED}`}
                            >
                              {item.is_active ? t('statusActive') : t('statusInactive')}
                            </span>
                          )}
                          <span className={`${PILL} ${messageTypeClass(item.message_type)}`}>
                            {t('messageType')}: {item.message_type}
                          </span>
                          <span className={`${PILL} ${messagePriorityClass(item.priority)}`}>
                            {t('messagePriority')}: {item.priority}
                          </span>
                        </div>
                        {(capabilities.canActivateSystemMessages ||
                          showDeactivateRow ||
                          capabilities.canDeleteSystemMessages) && (
                          <div className="flex shrink-0 flex-wrap items-center gap-2">
                            {capabilities.canActivateSystemMessages && item.is_active === false && (
                              <button
                                type="button"
                                disabled={messageActionId === item.id}
                                onClick={() => activateSystemMessage(item.id)}
                                className={BTN_PRIMARY}
                              >
                                {messageActionId === item.id ? t('loading') : t('activateMessage')}
                              </button>
                            )}
                            {showDeactivateRow && (
                              <button
                                type="button"
                                disabled={messageActionId === item.id}
                                onClick={() => deactivateSystemMessage(item.id)}
                                className={BTN_SECONDARY}
                              >
                                {messageActionId === item.id ? t('loading') : t('deactivateMessage')}
                              </button>
                            )}
                            {capabilities.canDeleteSystemMessages && (
                              <button
                                type="button"
                                disabled={messageActionId === item.id}
                                onClick={() => deleteSystemMessage(item.id)}
                                className={BTN_DANGER}
                              >
                                {messageActionId === item.id ? t('loading') : t('deleteMessage')}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      <p className={`mt-2 text-[13px] leading-relaxed whitespace-pre-wrap ${themeClasses.tableCell}`}>{item.content}</p>
                      <div className={`mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs ${themeClasses.textMuted}`}>
                        <span>
                          {t('messageWindow')}: {formatDateTime(item.start_date)} - {formatDateTime(item.end_date)}
                        </span>
                        <span>
                          {t('messageDirected')}: {formatDirectedTo(item)}
                        </span>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        <section className={`rounded-lg border ${themeClasses.cardBg}`}>
          <div className={themeClasses.sectionHead}>
            <h2 className={`text-[15px] font-semibold ${themeClasses.textPrimary}`}>{t('ingredientReportsTitle')}</h2>
          </div>
          {ingredientReports.length === 0 ? (
            <p className={`px-4 py-5 text-sm ${themeClasses.textMuted}`}>{t('ingredientReportsEmpty')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-[13px]">
                <thead className={themeClasses.tableHeader}>
                  <tr>
                    <th className="px-4 py-2 text-start text-[11px] font-semibold uppercase tracking-wide">{t('reportCreated')}</th>
                    <th className="px-4 py-2 text-start text-[11px] font-semibold uppercase tracking-wide">{t('reportFoodId')}</th>
                    <th className="px-4 py-2 text-start text-[11px] font-semibold uppercase tracking-wide">{t('reportType')}</th>
                    <th className="px-4 py-2 text-start text-[11px] font-semibold uppercase tracking-wide">{t('reportBy')}</th>
                    <th className="px-4 py-2 text-start text-[11px] font-semibold uppercase tracking-wide">{t('reportDescription')}</th>
                    <th className="px-4 py-2 text-start text-[11px] font-semibold uppercase tracking-wide">{t('reportSnapshot')}</th>
                  </tr>
                </thead>
                <tbody>
                  {ingredientReports.map((item) => (
                    <tr key={item.id} className={`border-t ${themeClasses.tableRow}`}>
                      <td className={`px-4 py-2.5 align-top ${themeClasses.tableCell}`}>{formatDateTime(item.created_at)}</td>
                      <td className={`px-4 py-2.5 align-top ${themeClasses.tableCell}`}>{item.food_id}</td>
                      <td className={`px-4 py-2.5 align-top ${themeClasses.tableCell}`}>{item.report_type}</td>
                      <td className={`px-4 py-2.5 align-top ${themeClasses.tableCell}`}>{item.reporter_user_code || '-'}</td>
                      <td className={`px-4 py-2.5 align-top ${themeClasses.tableCell}`}>{item.description || '-'}</td>
                      <td className={`px-4 py-2.5 align-top ${themeClasses.tableCell}`}>
                        {item.food_snapshot ? t('yes') : t('no')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

export default NotificationsPage;
